import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Form, FormControl, FormGroup, FormLabel, Alert, Spinner, Modal } from "react-bootstrap";
import ProductImg from "../../../images/product-img.png"
import Profile from "../../../images/profile-icon.svg"
import Call from "../../../images/call-icon.svg"
import Email from "../../../images/mail-icon.svg"
import LocationIcon from "../../../images/location-icon.svg"
import IconHome from "../../../images/icon-home.png"
import PhoneIcon from "../../../images/phone-icon.svg";
import AddressIcon from "../../../images/address-icon.svg";
import MailIcon from "../../../images/email-icon.png";
import { formatPhoneNumber } from '../../../utils/phoneFormatter';

// Import order-specific icons
import IconCupCake from "../../../images/orders/icon_cup-cake.png"
import IconGiftType from "../../../images/orders/icon_gift-type.png"
import IconCalendarFixed from "../../../images/orders/icon_calendar-fixed.png"
import IconCalendarOutlook from "../../../images/orders/icon_calendar-outlook.png"
import IconComments from "../../../images/orders/icon_comments.png"
import IconEnvelope from "../../../images/orders/icon_envelope.png"
import IconCallOrders from "../../../images/orders/icon_call.png"
import IconLocation from "../../../images/orders/icon_location.png"
import IconTransaction from "../../../images/orders/icon_trasaction.png"
import IconCard from "../../../images/orders/icon_card.png"
import IconCurrency from "../../../images/orders/icon_curr_icon.png"
import IconDeliveryVan from "../../../images/orders/icon_delivery-van.png"
import { Button } from 'react-bootstrap';
import { ordersApi, UpdateVerificationRequest, UpdateStatusRequest } from "../Orders/OrdersApi.tsx";
import { toast } from "react-toastify";
import { FaArrowLeftLong, FaDownload } from "react-icons/fa6";
import Swal from 'sweetalert2';
import { useApiLoader } from "../../../hooks/useApiLoader.tsx";
import "./Orders.scss"

interface OrderDetails {
  _id: string;
  orderId: string;
  orderDate: string;
  userName: string;
  occasion: string;
  type: string;
  occasionType?: string;
  date: string;
  milestoneEstimateDate?: string;
  milestoneConfirmed?: boolean;
  deathConfirmed?: boolean;
  giftStatus: string;
  paymentStatus: string;
  additionalComments: string;
  title?: string;
  recipientSnapshot?: {
    name: string;
    email: string;
    phoneNumber: string;
    alternatePhoneNumber?: string;
    relationship?: string;
    address1: string;
    city: string;
    state: string;
    zip: string;
    profileUrl?: string;
  };
  legacyContactSnapshot?: {
    name: string;
    email: string;
    phoneNumber: string;
    alternatePhoneNumber?: string;
    address1: string;
    city: string;
    state: string;
    zip: string;
  };
  primaryLegacyContact?: {
    name: string;
    email: string;
    phoneNumber: string;
    alternatePhoneNumber?: string;
    relationship?: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
  };
  userSnapshot?: {
    name: string;
    email: string;
    phoneNumber: string;
    address1: string;
    city: string;
    state: string;
    zip: string;
    ssn?: string;
  };
  userDetails?: {
    customerId: string;
    fullName: string;
    email: string;
    phoneNumber: string;
    address: {
      address1: string;
      address2: string;
      city: string;
      state: string;
      zip: string;
    };
    profileUrl?: string;
    accountStatus?: string;
  };
  giftSection?: {
    type: string;
    monetary?: { title: string; amount: number; imageUrl: string };
    giftCard?: { title: string; selectedAmount: number; imageUrl: string; sendMethod: string };
    physicalItem?: { title: string; price: number; description: string; imageUrl: string };
  };
  messageType?: string;
  deliverySchedule: string;
  recurringDetails?: {
    frequency: string;
    numberOfYears: number;
  };
  paymentDetails?: {
    transactionId: string;
    amountPaid: number;
    cardBrand: string;
    cardLast4: string;
    cardExpMonth: number;
    cardExpYear: number;
    paymentMethod?: string;
    amount?: number;
    paymentDate?: string;
  };
  messages?: Array<{
    title: string;
    content: string;
  }>;
  ssn?: string;
}

const PosthumousDetails = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { withLoader } = useApiLoader();
  
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);
  const [deathConfirmation, setDeathConfirmation] = useState<string>('');
  const [ssnRevealed, setSsnRevealed] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);

  // Handle audio download
  const handleDownloadAudio = async () => {
    if (!orderDetails) return;
    try {
      setDownloading(true);
      await ordersApi.downloadAudio(orderDetails._id);
      toast.success('Audio downloaded successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to download audio');
    } finally {
      setDownloading(false);
    }
  };

  // Fetch order details
  useEffect(() => {
    if (!orderId) {
      setError('Order ID is required');
      setLoading(false);
      return;
    }

    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use the loader for API call
        const response = await withLoader(
          () => ordersApi.getSingleOrder(orderId),
          'Loading posthumous gift details...'
        );
        
        if (response.status === 'Success' && response.data) {
          setOrderDetails(response.data);
          setDeathConfirmation(response.data.deathConfirmed ? 'Yes' : 'No');
        } else {
          setError(response.message || 'Failed to fetch order details');
        }
      } catch (error: any) {
        console.error('Error fetching order details:', error);
        setError(error.message || 'Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  // Handle death confirmation update with confirmation
  const handleDeathConfirmationUpdate = async () => {
    if (!orderDetails) return;

    const confirmationText = deathConfirmation === 'Yes' 
      ? 'confirm the death verification' 
      : 'mark death as not confirmed';
    
    const result = await Swal.fire({
      title: 'Update Death Confirmation?',
      text: `Are you sure you want to ${confirmationText}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3957A0',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Update Confirmation',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      setUpdating(true);

      const verificationData: UpdateVerificationRequest = {
        deathConfirmed: deathConfirmation === 'Yes',
        milestoneConfirmed: typeof orderDetails.milestoneConfirmed === 'boolean' ? orderDetails.milestoneConfirmed : false
      };

      const response = await ordersApi.updateGiftVerification(orderDetails._id, verificationData);

      if (response.status === 'Success') {
        toast.success('Death confirmation updated successfully');
        setOrderDetails(prev => prev ? { ...prev, deathConfirmed: deathConfirmation === 'Yes' } : null);
        
        Swal.fire({
          title: 'Success!',
          text: `Death confirmation has been ${deathConfirmation === 'Yes' ? 'confirmed' : 'updated'}`,
          icon: 'success',
          confirmButtonColor: '#3957A0'
        });
      } else {
        toast.error(response.message || 'Failed to update death confirmation');
        Swal.fire({
          title: 'Error!',
          text: response.message || 'Failed to update death confirmation',
          icon: 'error',
          confirmButtonColor: '#3957A0'
        });
      }
    } catch (error: any) {
      console.error('Error updating death confirmation:', error);
      toast.error(error.message || 'Failed to update death confirmation');
      
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to update death confirmation',
        icon: 'error',
        confirmButtonColor: '#3957A0'
      });
    } finally {
      setUpdating(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Format date and time for display
  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }) + ' - ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch {
      return 'Invalid Date';
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Capitalize text helper
  const capitalizeText = (text: string) => {
    if (!text) return 'N/A';
    // Capitalize first letter of each word
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Get initials from name (similar to Header component)
  const getInitials = (name: string) => {
    if (!name) return 'U'; // Default to 'U' for User if no name
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const getStatusClass = (status: string | undefined | null): string => {
    return typeof status === "string" && status.toLowerCase() === "active"
      ? "tag active"
      : "tag inactive";
  };

  // Format SSN for display
  const formatSSN = (ssn: string, revealed: boolean) => {
    if (!ssn) return '*** ** ****';
    
    // Remove any non-digits
    const cleanSSN = ssn.replace(/\D/g, '');
    
    if (revealed) {
      // Show full SSN: 123-45-6789
      return cleanSSN.replace(/(\d{3})(\d{2})(\d{4})/, '$1 $2 $3');
    } else {
      // Show only last 4 digits: *** ** 1234
      const lastFour = cleanSSN.slice(-4);
      return `*** ** ${lastFour}`;
    }
  };

  // Toggle SSN visibility
  const toggleSSNVisibility = () => {
    setSsnRevealed(!ssnRevealed);
  };

  // Get gift display details
  const getGiftDetails = () => {
    if (!orderDetails) return null;

    if (orderDetails.giftSection) {
      const section: any = orderDetails.giftSection;
      
      // Handle different gift types with proper image and amount logic
      switch (section.type) {
        case 'Monetary':
          return {
            image: section.monetary?.imageUrl || ProductImg,
            title: section.monetary?.title || orderDetails.title || 'Monetary Gift',
            description: `Monetary gift voucher for ${orderDetails.occasion}`,
            amount: section.monetary?.amount || 0
          };
        
        case 'GiftCard':
          return {
            image: section.giftCard?.imageUrl || ProductImg,
            title: section.giftCard?.title || orderDetails.title || 'Gift Card',
            description: `${section.giftCard?.title || 'Gift Card'} - ${section.giftCard?.sendMethod || 'Digital'} delivery for ${orderDetails.occasion}`,
            amount: section.giftCard?.selectedAmount || 0
          };
        
        case 'PhysicalItem':
          return {
            image: section.physicalItem?.imageUrl || ProductImg,
            title: section.physicalItem?.title || orderDetails.title || 'Physical Item',
            description: section.physicalItem?.description || `Physical gift item for ${orderDetails.occasion}`,
            amount: section.physicalItem?.price || 0
          };
        
        default:
          return {
            image: ProductImg,
            title: orderDetails.title || 'Gift Item',
            description: `${section.type} gift for ${orderDetails.occasion}`,
            amount: 0
          };
      }
    }

    // Handle message-only gifts
    const messageTitle = orderDetails.messages && orderDetails.messages.length > 0 
      ? orderDetails.messages[0].title 
      : 'Personal Message';
    
    return {
      image: ProductImg,
      title: messageTitle,
      description: `A heartfelt personal message for ${orderDetails.occasion}`,
      amount: 0
    };
  };

  // Loading state
  if (loading) {
    return (
      <section className="main-body">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading posthumous gift details...</p>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="main-body">
        <Alert variant="danger" className="m-4">
          <h5>Error Loading Order</h5>
          <p>{error}</p>
          <Button color="primary" onClick={() => navigate('/posthumousgifts')}>
            Back to Posthumous Gifts
          </Button>
        </Alert>
      </section>
    );
  }

  // No data state
  if (!orderDetails) {
    return (
      <section className="main-body">
        <Alert variant="warning" className="m-4">
          <h5>Order Not Found</h5>
          <p>The requested posthumous gift could not be found.</p>
          <Button color="primary" onClick={() => navigate('/posthumousgifts')}>
            Back to Posthumous Gifts
          </Button>
        </Alert>
      </section>
    );
  }

  const giftDetails = getGiftDetails();

  return (
    <>
      <section className="main-body">
        {/* Header Section */}
        <section className="order-header-section">
          <div className="container-fluid">
            <div className="row align-items-center mb-4">
              <div className="col-md-8">
                {/* <h1 className="order-title">Order ID: {orderDetails.orderId}</h1> */}
                <nav className="breadcrumb-nav">
                  <Link to="/posthumousgifts" className="text-decoration-none " style={{color:'#3957A0'}}>
                    <span><img src={IconHome} alt="home" /> <span className='my-auto'>Posthumous Gifts</span></span>
                  </Link>
                  <span className="mx-2 text-muted">›</span>
                  <span className="text-dark">{orderDetails.orderId}</span>
                </nav>
              </div>
              <div className="col-md-4 text-end">
                <Button color="secondary" className='bg-transparent border-0 fw-bold' style={{color:'#3957A0'}} onClick={() => navigate('/posthumousgifts')}>
                  <FaArrowLeftLong color='#3957A0'/> Back
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Profile Header */}
        {orderDetails.userDetails && (
          <div className='profile-area'>
            <div className='row justify-content-between' style={{ alignItems: "start" }}>
              <aside className='profile-block col-xl-3 col-lg-3'>
                <figure>
                  {orderDetails.userDetails.profileUrl ? (
                    <img
                      src={orderDetails.userDetails.profileUrl}
                      alt={orderDetails.userDetails.fullName || "Profile"}
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const fallbackDiv = document.createElement('div');
                          fallbackDiv.className = 'profile-circle-grid rounded-circle  text-white d-flex justify-content-center align-items-center';
                          fallbackDiv.style.width = '100px';
                          fallbackDiv.style.height = '100px';
                          fallbackDiv.textContent = getInitials(orderDetails.userDetails?.fullName || '');
                          parent.appendChild(fallbackDiv);
                        }
                      }}
                    />
                  ) : (
                    <div className="profile-circle-grid rounded-circle  text-white d-flex justify-content-center align-items-center" style={{ width: '100px', height: '100px' }}>
                      {getInitials(orderDetails.userDetails.fullName)}
                    </div>
                  )}
                </figure>
                <div className="profile-cont">
                  <h3>{capitalizeText(orderDetails.userDetails.fullName) || "No name provided"}</h3>
                  <span className={getStatusClass(orderDetails.userDetails.accountStatus)}>
                    {capitalizeText(orderDetails.userDetails.accountStatus) || "Unknown"}
                  </span>
                </div>
              </aside>
              <aside className='profile-det col-xl-3 col-lg-3 mt-3'>
                <div className='profile-det-in'>
                  <div className='icon'>
                    <img src={MailIcon} alt='Email' />
                  </div>
                  <div className='profile-det-cont'>
                    <h3>Email</h3>
                    <p>{orderDetails.userDetails?.email || orderDetails.userSnapshot?.email || "Not provided"}</p>
                  </div>
                </div>
              </aside>
              <aside className='profile-det col-xl-3 col-lg-3 mt-3'>
                <div className='profile-det-in'>
                  <div className='icon'>
                    <img src={AddressIcon} alt='Phone' />
                  </div>
                  <div className='profile-det-cont'>
                    <h3>Phone</h3>
                    <p>{formatPhoneNumber(orderDetails.userDetails.phoneNumber) || "Not provided"}</p>
                  </div>
                </div>
              </aside>
              <aside className='profile-det col-xl-3 col-lg-3 mt-3'>
                <div className='profile-det-in'>
                  <div className='icon'>
                    <img src={AddressIcon} alt='Address' />
                  </div>
                  <div className='profile-det-cont'>
                    <h3>Address</h3>
                    <p>
                      {orderDetails.userDetails.address?.address1 || orderDetails.userDetails.address?.city || orderDetails.userDetails.address?.state || orderDetails.userDetails.address?.zip
                        ? [
                            orderDetails.userDetails.address?.address1,
                            orderDetails.userDetails.address?.city,
                            orderDetails.userDetails.address?.state,
                            orderDetails.userDetails.address?.zip
                          ].filter(Boolean).join(', ')
                        : 'Not provided'
                      }
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        )}

        {/* Combined Details Block */}
        <div className="details-block">
          <div className="details-block-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="section-title mb-0 fw-bold" style={{fontSize:'20px'}}>Gift Information</h2>
              <div className="d-flex gap-2 align-items-center">
                <span className={`badge ${
                  orderDetails.giftStatus === 'Delivered' ? 'bg-success' :
                  orderDetails.giftStatus === 'Shipped' ? 'bg-info' :
                  orderDetails.giftStatus === 'Processing' ? 'bg-warning text-dark' :
                  'bg-secondary'
                }`}>
                  {orderDetails.giftStatus || 'Pending'}
                </span>
                {orderDetails.messageType === 'Physical' && (
                  <Button
                    size="sm"
                    disabled={downloading}
                    onClick={handleDownloadAudio}
                    style={{
                      backgroundColor: '#3957A0',
                      borderColor: '#3957A0',
                      borderRadius: '8px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <FaDownload size={14} />
                    {downloading ? 'Downloading...' : 'Download Audio'}
                  </Button>
                )}
              </div>
            </div>
            
            <div className="row">
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCupCake} alt="Gift Title" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Gift Title</label>
                       <p className="fw-bold mb-0">{orderDetails.title || giftDetails?.title || 'Gift Item'}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconGiftType} alt="Gift Type" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Gift Type</label>
                      <p className="fw-bold mb-0">{orderDetails.giftSection?.type || 'Regular Gift'}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCalendarFixed} alt="Set Occasion" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Set Occasion</label>
                      <p className="fw-bold mb-0">{capitalizeText(orderDetails.occasion)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="row">
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconDeliveryVan} alt="Recurring Schedule" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Delivery Schedule</label>
                      <p className="fw-bold mb-0">
                    {orderDetails.recurringDetails ? 
                      `${orderDetails.recurringDetails.frequency} (${orderDetails.recurringDetails.numberOfYears} Years)` : 
                      orderDetails.deliverySchedule}
                  </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCalendarFixed} alt="Set Delivery Date" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Occasion Date</label>
                      <p className="fw-bold mb-0">{new Date(orderDetails.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCalendarOutlook} alt="Recurring End Date" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Recurring End Date</label>
                      <p className="fw-bold mb-0">{formatDate(orderDetails.milestoneEstimateDate || orderDetails.date)}</p>
                    </div>
                  </div>
                </div>
              </div> */}

              <div className="col-md-12">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconComments} alt="Additional Comments" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">We kindly request that you place a call upon your arrival to notify us of the delivery</label>
                      <p className="fw-bold mb-0">{orderDetails.additionalComments}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <hr/>
            {/* Recipient & Delivery Information */}
            <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="section-title mb-0 fw-bold" style={{fontSize:'20px'}}>Recipient & Delivery Information</h2>
          </div>
            
            <div className="row">
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Profile} alt="Name" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Name</label>
                      <p className="fw-bold mb-0">{capitalizeText(orderDetails.recipientSnapshot?.name || orderDetails.userName)}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Email} alt="Email" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Email</label>
                      <p className="fw-bold mb-0">{orderDetails.recipientSnapshot?.email || orderDetails.userSnapshot?.email}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Call} alt="Phone" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Phone</label>
                      <p className="fw-bold mb-0">{formatPhoneNumber(orderDetails.recipientSnapshot?.phoneNumber || orderDetails.userSnapshot?.phoneNumber)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Call} alt="Alternate Phone" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Alternate Phone</label>
                      <p className="fw-bold mb-0">{formatPhoneNumber(orderDetails.recipientSnapshot?.alternatePhoneNumber) || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-8">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={LocationIcon} alt="Address" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Address</label>
                      <p className="fw-bold mb-0">
                        {(orderDetails.recipientSnapshot?.address1 || orderDetails.userSnapshot?.address1)}<br />
                        {(orderDetails.recipientSnapshot?.city || orderDetails.userSnapshot?.city)}, {' '}
                        {(orderDetails.recipientSnapshot?.state || orderDetails.userSnapshot?.state)} {' '}
                        {(orderDetails.recipientSnapshot?.zip || orderDetails.userSnapshot?.zip)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>

        {/* Payment Information Section - Matching Figma with product display */}
         {orderDetails.paymentDetails?.amountPaid !== 0 && (
          <>
            <hr/>
            <div className="row">
              {/* Product Information */}
              <div className="col-md-5" >
            <div className="mt-0">
              <div className="" style={{backgroundColor:'#EFF4FF', padding:'20px', borderRadius:'8px'}}>
                
                <div className="product-payment-display">
                  <div className="row">
                    {/* Product Image and Details */}
                    <div className="col-md-4">
                      <div className="product-image-container">
                        {giftDetails && (
                          <img 
                            src={giftDetails.image} 
                            alt="Gift" 
                            className="img-fluid rounded product-image"
                            onError={(e) => {
                              e.currentTarget.src = ProductImg;
                            }}
                          />
                        )}
                      </div>
                    </div>
                    
                    <div className="col-md-8">
                      <div className="product-details-section">
                        <h3 className="product-title" style={{color:'#382F2D'}}>
                          {capitalizeText((() => {
                            const giftType = orderDetails.giftSection?.type;
                            if (giftType === 'Monetary') {
                              return orderDetails.giftSection?.monetary?.title || giftDetails?.title || 'Gift Item';
                            } else if (giftType === 'PhysicalItem') {
                              return orderDetails.giftSection?.physicalItem?.title || giftDetails?.title || 'Gift Item';
                            } else if (giftType === 'GiftCard') {
                              return orderDetails.giftSection?.giftCard?.title || giftDetails?.title || 'Gift Item';
                            } else {
                              return giftDetails?.title || orderDetails.title || 'Gift Item';
                            }
                          })())}
                        </h3>
                        <h2 className="product-price mb-2" style={{color:'#3957A0'}}>
                          {giftDetails?.amount ? formatCurrency(giftDetails.amount) : '$21.40'}
                        </h2>
                        <p className="product-description text-muted">
                          {giftDetails?.description || 'Joyful Petal Symphony celebrates any love for flowers. Each bloom expresses appreciation and respect through a lush landscape.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Payment Details */}
          <div className="col-md-7">
            
            <div className='row '>
              <h3 className="section-title mb-4 fw-bold" style={{fontSize:'20px'}}>Payment Information</h3>
              <div className='col-md-6'>
                <div className="info-item d-flex align-items-center mb-3">
                  <img src={IconTransaction} alt="" className="me-3" width="24" height="24" />
                  <div>
                    <label className="form-label mb-1 text-muted">Transaction ID</label>
                    <p className="fw-bold">{orderDetails.paymentDetails?.transactionId || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className='col-md-6'>
                <div className="info-item d-flex align-items-center mb-3">
                  <img src={IconCalendarOutlook} alt="" className="me-3" width="24" height="24" />
                  <div>
                    <label className="form-label mb-1 text-muted">Date & Time</label>
                    <p className="fw-bold">{formatDateTime(orderDetails.orderDate)}</p>
                  </div>
                </div>
              </div>

              <div className='col-md-6'>
                <div className="info-item d-flex align-items-center mb-3">
                  <img src={IconCard} alt="" className="me-3" width="24" height="24" />
                  <div>
                    <label className="form-label mb-1 text-muted">Payment Method</label>
                    <p className="fw-bold">{orderDetails.paymentDetails?.cardBrand ? `${orderDetails.paymentDetails.cardBrand.toUpperCase()} **** ${orderDetails.paymentDetails.cardLast4}` : 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className='col-md-6'>
                <div className="info-item d-flex align-items-center mb-3">
                  <img src={IconCurrency} alt="" className="me-3" width="24" height="24" />
                  <div>
                    <label className="form-label mb-1 text-muted">Paid Amount</label>
                    <p className={`fw-bold ${(orderDetails.paymentDetails?.amountPaid || orderDetails.paymentDetails?.amount) ? 'text-success fs-5' : ''}`}>
                      {orderDetails.paymentDetails?.amountPaid ? formatCurrency(orderDetails.paymentDetails.amountPaid) : (orderDetails.paymentDetails?.amount ? formatCurrency(orderDetails.paymentDetails.amount) : 'N/A')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
          </>
         )}

 {/* Legacy Contact Information */}
 <hr/>

 <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="section-title mb-0 fw-bold" style={{fontSize:'20px'}}>Legacy Contact Information</h2>
          </div>
            
            <div className="row">
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Profile} alt="Name" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Name</label>
                      <p className="fw-bold mb-0">{capitalizeText(orderDetails.primaryLegacyContact?.name || orderDetails.legacyContactSnapshot?.name) || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Profile} alt="Relationship" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Relationship</label>
                      <p className="fw-bold mb-0">{capitalizeText(orderDetails.primaryLegacyContact?.relationship) || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Email} alt="Email" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Email</label>
                      <p className="fw-bold mb-0">{orderDetails.primaryLegacyContact?.email || orderDetails.legacyContactSnapshot?.email || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Call} alt="Phone" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Phone</label>
                      <p className="fw-bold mb-0">{formatPhoneNumber(orderDetails.primaryLegacyContact?.phoneNumber || orderDetails.legacyContactSnapshot?.phoneNumber) || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Call} alt="Alternate Phone" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Alternate Phone</label>
                      <p className="fw-bold mb-0">{formatPhoneNumber(orderDetails.primaryLegacyContact?.alternatePhoneNumber || orderDetails.legacyContactSnapshot?.alternatePhoneNumber) || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={LocationIcon} alt="Address" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Address</label>
                      <p className="fw-bold mb-0">
                        {orderDetails.primaryLegacyContact?.address1 || orderDetails.legacyContactSnapshot?.address1 || 'N/A'}<br />
                        {orderDetails.primaryLegacyContact?.city || orderDetails.legacyContactSnapshot?.city || ''}, {orderDetails.primaryLegacyContact?.state || orderDetails.legacyContactSnapshot?.state || ''} {orderDetails.primaryLegacyContact?.zip || orderDetails.legacyContactSnapshot?.zip || ''}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
<hr/>
            {/* SSN Section - Separate card as shown in Figma */}
            <div className="row mt-4">
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Profile} alt="SSN" width="32" className="gift-icon" />
                    </div>
                    <div className="w-100 d-flex">
                      <div>
                        <label className="form-label mb-0 text-muted">SSN Number</label>
                          {orderDetails.ssn ? (
                            <p className="fw-bold mb-0">{formatSSN(orderDetails.ssn, ssnRevealed)}</p>
                          ) : (
                            <p className="fw-bold mb-0 text-muted">SSN not available</p>
                          )}
                      </div>
                      {orderDetails.ssn && (
                        <div className='my-auto'>
                          <Button
                            color={ssnRevealed ? 'warning' : 'primary'}
                            size="sm"
                            className="ms-2"
                            onClick={toggleSSNVisibility}
                          >
                            {ssnRevealed ? 'Hide' : 'Reveal'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>

        {/* Death Confirmation Section - Key Posthumous Feature */}
         

         <div className="" style={{backgroundColor:'#EFF4FF', padding:'20px', borderRadius:'8px', marginTop:'20px', border:'1px solid #CFD9E3'}}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="section-title mb-0 fw-bold" style={{fontSize:'16px'}}>Death Confirmation</h2>
          </div>
            
            <div className="row align-items-end">
              <div>
                <FormGroup className='form-group mb-0'>
                  <div className="d-flex gap-3">
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="radio" 
                        name="deathConfirmation" 
                        id="deathYes"
                        value="Yes"
                        checked={deathConfirmation === 'Yes'}
                        onChange={(e) => setDeathConfirmation(e.target.value)}
                        disabled={updating}
                      />
                      <label className="form-check-label fw-bold mt-1" htmlFor="deathYes">
                        Yes
                      </label>
                    </div>
                    <div className="form-check">
                      <input 
                        className="form-check-input" 
                        type="radio" 
                        name="deathConfirmation" 
                        id="deathNo"
                        value="No"
                        checked={deathConfirmation === 'No'}
                        onChange={(e) => setDeathConfirmation(e.target.value)}
                        disabled={updating}
                      />
                      <label className="form-check-label fw-bold mt-1" htmlFor="deathNo">
                        No
                      </label>
                    </div>
                    <Button 
                  color="primary" 
                  className="btn-primary"
                  disabled={updating}
                  onClick={handleDeathConfirmationUpdate}
                >
                  {updating ? 'Updating...' : 'Update'}
                </Button>
                  </div>
                </FormGroup>
                
              </div>
              
            </div>
          </div>



          </div>

    
        </div>
        
        
      
      </section>
    </>
  );
};

export default PosthumousDetails;